import { useEffect, useState, type FormEvent } from 'react';
import type { InvestmentCompany, InvestmentStatus, ValuationMethod } from '../types';
import { GlobalButton } from './GlobalButton';
import { GlobalDropdown, type GlobalDropdownOption } from './GlobalDropdown';
import { GlobalInput } from './GlobalInput';
import { normalizeWebsiteUrl } from '../utils/favicon';
import { normalizeCountryCode } from '../utils/investmentCompanyUi';

type Props = {
  company?: InvestmentCompany | null;
  onSubmit: (next: InvestmentCompany) => void;
  onCancel: () => void;
  onDelete?: () => void;
};

const valuationOptions: GlobalDropdownOption[] = [
  { value: 'manual', label: 'Manual' },
  { value: 'revenueMultiple', label: 'Revenue multiple' },
  { value: 'ebitdaMultiple', label: 'EBITDA multiple' },
];

const statusOptions: GlobalDropdownOption[] = [
  { value: 'active', label: 'Active' },
  { value: 'exit', label: 'Exit' },
  { value: 'dead', label: 'Dead' },
];

/** Form draft keeps `investmentDate` / `statusDate` separate (date inputs YYYY-MM-DD). */
type CompanyDraft = Omit<
  InvestmentCompany,
  'id' | 'createdAt' | 'updatedAt' | 'investmentDate' | 'statusDate'
>;

function empty(): CompanyDraft {
  return {
    name: '',
    websiteUrl: undefined,
    countryCode: undefined,
    foundingYear: undefined,
    industry: '',
    ownershipPercentage: 0,
    ticketSize: 0,
    investmentStatus: 'active',
    exitTotalEuros: undefined,
    valuationMethod: 'manual',
    manualValuation: undefined,
    revenueMultiple: undefined,
    ebitdaMultiple: undefined,
    exitProbabilityThisYear: 0,
    expectedDividendThisYear: 0,
    expectedReturnLeadTime: undefined,
    expectedReturnMultiple: undefined,
  };
}

function normalizeCompanyDraft(c: InvestmentCompany): CompanyDraft {
  const {
    id: _id,
    createdAt: _ca,
    updatedAt: _ua,
    investmentDate: _idt,
    statusDate: _sd,
    ...rest
  } = c;
  const st = c.investmentStatus;
  const investmentStatus: InvestmentStatus =
    st === 'exit' || st === 'dead' || st === 'active' ? st : 'active';
  return {
    ...rest,
    investmentStatus,
    ticketSize: typeof c.ticketSize === 'number' && Number.isFinite(c.ticketSize) ? c.ticketSize : 0,
    exitTotalEuros:
      investmentStatus === 'exit' &&
      typeof c.exitTotalEuros === 'number' &&
      Number.isFinite(c.exitTotalEuros)
        ? c.exitTotalEuros
        : undefined,
  };
}

function isoToInputDate(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function todayInputDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function inputDateToIso(inputDate: string): string {
  const fallback = new Date().toISOString();
  if (!inputDate) return fallback;
  const d = new Date(`${inputDate}T12:00:00`);
  return Number.isNaN(d.getTime()) ? fallback : d.toISOString();
}

export function InvestmentCompanyForm({ company, onSubmit, onCancel, onDelete }: Props) {
  const [draft, setDraft] = useState<CompanyDraft>(() =>
    company ? normalizeCompanyDraft(company) : empty()
  );
  const [investmentDateInput, setInvestmentDateInput] = useState<string>(() =>
    isoToInputDate(company?.investmentDate) || todayInputDate()
  );
  const [statusDateInput, setStatusDateInput] = useState<string>(() => {
    const st = company?.investmentStatus;
    const eff = st === 'exit' || st === 'dead' || st === 'active' ? st : 'active';
    if (!company || eff === 'active') return '';
    return isoToInputDate(company.statusDate) || todayInputDate();
  });
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(company ? normalizeCompanyDraft(company) : empty());
    setInvestmentDateInput(isoToInputDate(company?.investmentDate) || todayInputDate());
    const st = company?.investmentStatus;
    const eff = st === 'exit' || st === 'dead' || st === 'active' ? st : 'active';
    if (!company || eff === 'active') {
      setStatusDateInput('');
    } else {
      setStatusDateInput(isoToInputDate(company.statusDate) || todayInputDate());
    }
    setFormError(null);
  }, [company]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const now = new Date().toISOString();
    const base = company
      ? company
      : ({ id: '', createdAt: now, updatedAt: now } as InvestmentCompany);

    const num = (v: unknown, fallback = 0) => {
      const n = typeof v === 'number' ? v : Number(v);
      return Number.isFinite(n) ? n : fallback;
    };

    const status: InvestmentStatus =
      draft.investmentStatus === 'exit' || draft.investmentStatus === 'dead'
        ? draft.investmentStatus
        : 'active';

    if (status === 'exit' || status === 'dead') {
      if (!statusDateInput.trim()) {
        setFormError('Add a status date for exit or dead.');
        return;
      }
    }
    if (status === 'exit') {
      const exitTot = num(draft.exitTotalEuros, Number.NaN);
      if (!Number.isFinite(exitTot) || exitTot < 0) {
        setFormError('Exit requires a total amount in euros (0 or greater).');
        return;
      }
    }

    setFormError(null);

    const fy =
      draft.foundingYear === undefined || draft.foundingYear === null
        ? undefined
        : num(draft.foundingYear);

    const country = normalizeCountryCode(
      typeof draft.countryCode === 'string' ? draft.countryCode : String(draft.countryCode ?? '')
    );

    onSubmit({
      ...base,
      ...draft,
      websiteUrl: normalizeWebsiteUrl(
        typeof draft.websiteUrl === 'string' ? draft.websiteUrl : String(draft.websiteUrl ?? '')
      ),
      countryCode: country,
      investmentStatus: status,
      statusDate:
        status === 'exit' || status === 'dead' ? inputDateToIso(statusDateInput) : undefined,
      exitTotalEuros: status === 'exit' ? num(draft.exitTotalEuros) : undefined,
      foundingYear: fy && fy > 1800 && fy < 2100 ? Math.round(fy) : undefined,
      ownershipPercentage: num(draft.ownershipPercentage),
      ticketSize: num(draft.ticketSize),
      investmentDate: inputDateToIso(investmentDateInput),
      manualValuation:
        draft.manualValuation === undefined || draft.manualValuation === null
          ? undefined
          : num(draft.manualValuation),
      revenueMultiple:
        draft.revenueMultiple === undefined || draft.revenueMultiple === null
          ? undefined
          : num(draft.revenueMultiple),
      ebitdaMultiple:
        draft.ebitdaMultiple === undefined || draft.ebitdaMultiple === null
          ? undefined
          : num(draft.ebitdaMultiple),
      exitProbabilityThisYear: num(draft.exitProbabilityThisYear),
      expectedDividendThisYear: num(draft.expectedDividendThisYear),
      expectedReturnLeadTime:
        draft.expectedReturnLeadTime === undefined || draft.expectedReturnLeadTime === null
          ? undefined
          : num(draft.expectedReturnLeadTime),
      expectedReturnMultiple:
        draft.expectedReturnMultiple === undefined || draft.expectedReturnMultiple === null
          ? undefined
          : num(draft.expectedReturnMultiple),
      valuationMethod: draft.valuationMethod as ValuationMethod,
      updatedAt: now,
      createdAt: company?.createdAt ?? now,
      id: company?.id ?? base.id,
    });
  }

  const method = draft.valuationMethod;
  const status = draft.investmentStatus ?? 'active';

  return (
    <form className="sidebarFormStack" onSubmit={handleSubmit}>
      {formError ? (
        <p className="sidebarFormError" role="alert">
          {formError}
        </p>
      ) : null}
      <GlobalInput
        label="Name"
        value={draft.name}
        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        required
      />
      <GlobalInput
        label="Website URL"
        value={draft.websiteUrl ?? ''}
        onChange={(e) => setDraft({ ...draft, websiteUrl: e.target.value || undefined })}
        placeholder="https:// or example.com"
      />
      <GlobalInput
        label="Industry"
        value={draft.industry}
        onChange={(e) => setDraft({ ...draft, industry: e.target.value })}
      />
      <GlobalInput
        label="Country (ISO code)"
        value={draft.countryCode ?? ''}
        onChange={(e) =>
          setDraft({
            ...draft,
            countryCode: e.target.value ? e.target.value.toUpperCase().slice(0, 2) : undefined,
          })
        }
        placeholder="e.g. DE"
        maxLength={2}
        autoCapitalize="characters"
      />
      <GlobalInput
        label="Founding year"
        type="number"
        value={draft.foundingYear ?? ''}
        onChange={(e) =>
          setDraft({
            ...draft,
            foundingYear: e.target.value === '' ? undefined : Number(e.target.value),
          })
        }
      />
      <GlobalInput
        label="Investment date"
        type="date"
        value={investmentDateInput}
        onChange={(e) => setInvestmentDateInput(e.target.value)}
        required
      />
      <GlobalInput
        label="Ownership %"
        type="number"
        step="any"
        value={draft.ownershipPercentage}
        onChange={(e) => setDraft({ ...draft, ownershipPercentage: Number(e.target.value) })}
      />
      <GlobalInput
        label="Ticket size (€)"
        type="number"
        step="any"
        value={draft.ticketSize}
        onChange={(e) => setDraft({ ...draft, ticketSize: Number(e.target.value) })}
      />

      <GlobalDropdown
        label="Investment status"
        value={status}
        onChange={(v) => {
          const st = v as InvestmentStatus;
          setFormError(null);
          if (st === 'active') {
            setStatusDateInput('');
            setDraft({ ...draft, investmentStatus: st, exitTotalEuros: undefined });
          } else if (st === 'dead') {
            setDraft({ ...draft, investmentStatus: st, exitTotalEuros: undefined });
            setStatusDateInput((prev) => prev || todayInputDate());
          } else {
            setDraft({ ...draft, investmentStatus: st });
            setStatusDateInput((prev) => prev || todayInputDate());
          }
        }}
        options={statusOptions}
      />

      {status === 'exit' || status === 'dead' ? (
        <GlobalInput
          label={status === 'exit' ? 'Exit / write-off date' : 'Write-off date'}
          type="date"
          value={statusDateInput}
          onChange={(e) => setStatusDateInput(e.target.value)}
          required
        />
      ) : null}

      {status === 'exit' ? (
        <GlobalInput
          label="Total exit proceeds (€)"
          type="number"
          step="any"
          min={0}
          value={draft.exitTotalEuros ?? ''}
          onChange={(e) =>
            setDraft({
              ...draft,
              exitTotalEuros: e.target.value === '' ? undefined : Number(e.target.value),
            })
          }
          required
        />
      ) : null}

      <GlobalDropdown
        label="Valuation method"
        value={draft.valuationMethod}
        onChange={(v) => setDraft({ ...draft, valuationMethod: v as ValuationMethod })}
        options={valuationOptions}
      />

      {method === 'manual' ? (
        <GlobalInput
          label="Manual valuation (€)"
          type="number"
          step="any"
          value={draft.manualValuation ?? ''}
          onChange={(e) =>
            setDraft({
              ...draft,
              manualValuation: e.target.value === '' ? undefined : Number(e.target.value),
            })
          }
        />
      ) : null}

      {method === 'revenueMultiple' ? (
        <GlobalInput
          label="Revenue multiple (×)"
          type="number"
          step="any"
          value={draft.revenueMultiple ?? ''}
          onChange={(e) =>
            setDraft({
              ...draft,
              revenueMultiple: e.target.value === '' ? undefined : Number(e.target.value),
            })
          }
        />
      ) : null}

      {method === 'ebitdaMultiple' ? (
        <GlobalInput
          label="EBITDA multiple (×)"
          type="number"
          step="any"
          value={draft.ebitdaMultiple ?? ''}
          onChange={(e) =>
            setDraft({
              ...draft,
              ebitdaMultiple: e.target.value === '' ? undefined : Number(e.target.value),
            })
          }
        />
      ) : null}

      <GlobalInput
        label="Exit probability this year %"
        type="number"
        step="any"
        value={draft.exitProbabilityThisYear}
        onChange={(e) => setDraft({ ...draft, exitProbabilityThisYear: Number(e.target.value) })}
      />
      <GlobalInput
        label="Expected dividend this year (€)"
        type="number"
        step="any"
        value={draft.expectedDividendThisYear}
        onChange={(e) => setDraft({ ...draft, expectedDividendThisYear: Number(e.target.value) })}
      />

      <div className="formFieldDivider" style={{ borderTop: '1px solid var(--global-separator-color)', margin: '16px 0', paddingTop: '16px' }}>
        <p className="muted" style={{ fontSize: '0.875rem', marginBottom: '12px' }}>
          Future returns projection — set expected timeline and multiple to see when returns may occur.
        </p>
      </div>
      <GlobalInput
        label="Expected return lead time (years)"
        type="number"
        step="0.5"
        min={0}
        value={draft.expectedReturnLeadTime ?? ''}
        onChange={(e) =>
          setDraft({
            ...draft,
            expectedReturnLeadTime: e.target.value === '' ? undefined : Number(e.target.value),
          })
        }
        placeholder="e.g. 5"
      />
      <GlobalInput
        label="Expected return multiple (×)"
        type="number"
        step="0.1"
        min={0}
        value={draft.expectedReturnMultiple ?? ''}
        onChange={(e) =>
          setDraft({
            ...draft,
            expectedReturnMultiple: e.target.value === '' ? undefined : Number(e.target.value),
          })
        }
        placeholder="e.g. 10"
      />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'space-between', marginTop: 8 }}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {onDelete ? (
            <GlobalButton type="button" variant="secondary" destructive onClick={onDelete}>
              Delete
            </GlobalButton>
          ) : null}
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <GlobalButton type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </GlobalButton>
          <GlobalButton type="submit" variant="primary">
            Save
          </GlobalButton>
        </div>
      </div>
    </form>
  );
}
